import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { listNoteStyles, type NoteStyle } from '@/lib/api'
import { cn } from '@/lib/utils'

// Picks which saved note style rides with the next message. The selection is
// resolved once styles load: a restored id that no longer exists falls back to
// the business's default style, then to none. Styles themselves are edited in
// Settings → AI assistant or through chat.

const NOTE_STYLES_PAGE = '/settings/assistant'

interface NoteStylePickerProps {
  selectedId: string | null
  onSelect: (id: string | null) => void
  disabled?: boolean
}

export function NoteStylePicker({ selectedId, onSelect, disabled }: NoteStylePickerProps) {
  const [styles, setStyles] = useState<NoteStyle[] | null>(null)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const initialisedRef = useRef(false)

  useEffect(() => {
    let live = true
    listNoteStyles({ limit: 100 })
      .then((page) => { if (live) setStyles(page.items) })
      .catch(() => { if (live) setStyles([]) })
    return () => { live = false }
  }, [])

  useEffect(() => {
    if (styles === null || initialisedRef.current) return
    initialisedRef.current = true
    const stillExists = selectedId !== null && styles.some((s) => s.id === selectedId)
    if (!stillExists) onSelect(styles.find((s) => s.is_default)?.id ?? null)
  }, [styles, selectedId, onSelect])

  const chipClass =
    'rounded-2xl border border-border bg-muted px-3 py-1.5 text-[13px] font-medium text-muted-foreground disabled:opacity-40'

  if (styles !== null && styles.length === 0) {
    return (
      <button type="button" className={chipClass} onClick={() => navigate(NOTE_STYLES_PAGE)} disabled={disabled}>
        ✍️ Add note styles
      </button>
    )
  }

  const selected = styles?.find((s) => s.id === selectedId) ?? null

  return (
    <div className="relative">
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            aria-label="Close note style menu"
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-full left-0 z-20 mb-1.5 flex max-h-72 w-60 flex-col overflow-y-auto rounded-lg border border-border bg-card shadow-[0_4px_12px_rgba(0,0,0,0.10)]">
            <button
              type="button"
              className="flex cursor-pointer items-center justify-between px-3.5 py-[11px] text-left"
              onClick={() => { onSelect(null); setOpen(false) }}
            >
              <span className="text-[14px] text-foreground">No style</span>
              {selectedId === null && <span className="text-[13px] text-muted-foreground">✓</span>}
            </button>
            {styles?.map((s) => (
              <button
                key={s.id}
                type="button"
                className="flex cursor-pointer items-center justify-between gap-2 border-t border-muted px-3.5 py-[11px] text-left"
                onClick={() => { onSelect(s.id); setOpen(false) }}
              >
                <span className="min-w-0 truncate text-[14px] text-foreground">{s.name}</span>
                {selectedId === s.id && <span className="shrink-0 text-[13px] text-muted-foreground">✓</span>}
              </button>
            ))}
            <button
              type="button"
              className="cursor-pointer border-t border-muted px-3.5 py-[11px] text-left text-[13px] text-muted-foreground"
              onClick={() => { setOpen(false); navigate(NOTE_STYLES_PAGE) }}
            >
              Manage styles…
            </button>
          </div>
        </>
      )}
      <button
        type="button"
        className={cn(chipClass, 'max-w-56 truncate')}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || styles === null}
        aria-label="Choose note style"
      >
        ✍️ Style: {selected ? selected.name : 'None'}
      </button>
    </div>
  )
}
